// What a teacher lands on: what needs them now, and where the class is in the plan.
//
// THE SHAPE OF THIS FILE IS AN ARGUMENT. The old sidebar named our pipeline — Student work,
// How work arrived, Release are `artifact`, `intake_manifest` and `artifact_delivery` wearing
// friendlier words. A teacher does not have a pipeline; they have a week. They need to know what
// is waiting for a human, and whether the plan is working. Those are the two bands.
//
// The point of the product is that scores change the plan (and nothing else here matters if that
// loop is invisible), so the plan is on the same screen as the work rather than a click away.
//
// WHAT THIS DELIBERATELY CANNOT EXPRESS, carried over from AssignmentHome:
//  * No average, no completion percentage, no per-class score. `Signal` counts PAPERS AT A LEVEL,
//    because a mean over criterion levels is a number nobody assigned.
//  * Nothing disappears at zero. A band with no work says so.
//  * Work that needs a human is NAMED. "Two need attention" is not something anyone can act on.

/** Why a thing needs a human. The vocabulary is closed: every value maps to state the database
 *  already records, so nothing here is a judgement the UI invented.
 *
 *  disagreement  — artifact_composition.prior_rater_mismatch
 *  held          — composition.needs_human, or a scoring_trace outcome of `gated`
 *  unnamed       — artifact.state = 'unbound': a paper nobody is named on
 *  not_scorable  — artifact.state = 'not_scorable', with its state_reason_code
 *  release       — a set is reviewed and has not gone back
 */
export type NeedKind =
  | "disagreement" | "held" | "unnamed" | "not_scorable" | "message_held" | "release";

export interface Need {
  kind: NeedKind;
  /** Which cohort this is from. The queue spans every cohort the teacher staffs — it is the list of
   *  things to resolve, and a teacher resolving them does not care which room they came from — so
   *  every row has to say, or "Maya R. · Counterclaims" is ambiguous the moment there are two. */
  class_label: string;
  /** The person or set this is about: a student's roster name, or the set's name. Never a key
   *  dressed up as a name — an unrostered student shows their identifier, which is what they are. */
  subject: string;
  /** The specific thing, in the teacher's words: "Counterclaims", "Op-ed, Period 4". */
  detail: string;
  /** Where acting on it happens. */
  href: string;
  /** For `missing` and `release`, the two counts that make the sentence true. */
  of?: { have: number; expected: number };
}

/** A FOLDER OF STUDENT WORK: a lesson taught to a cohort in one iteration.
 *
 *  What a teacher means by "the drafts" or "the final op-eds". `learner_activity` in the
 *  database — the name nobody says out loud.
 *
 *  IT IS NOT A GOOGLE DRIVE FOLDER, and the two are easy to fold into one because in the common
 *  case there is one of each. They are different objects:
 *
 *    - the folder of student work is OURS. It exists as soon as a teacher says this lesson's work
 *      for this cohort is a thing, it holds the papers, the scores and the release decision, and it
 *      is what the screens are organised around.
 *    - a Google Drive folder is WHERE ITS WORK COMES FROM, and today it is the way work gets in.
 *      It is still a different fact about the folder of student work: it can be added later or
 *      changed, and work can arrive without one — the demo's Final holds three papers and has no
 *      Drive folder, because a batch job read them from a local path.
 *
 *  So `folder_url` being null says nothing about whether there is work here, and copy that says
 *  "add the folder" has to say WHICH. */
export interface WorkFolder {
  activity_id: string;
  /** WHOSE work this is. A lesson's folders can span the teacher's sections of the course, and
   *  two folders called "Final" under one lesson are two different cohorts. */
  section_id: string;
  section_label: string;
  /** Which collection this is, in the teacher's word: "draft", "final". */
  iteration: string | null;
  state: PlanLesson["state"];
  handed_in?: { have: number; expected: number } | null;
  href: string;
  /** Where this collection's work is read from, and what the teacher calls it. Null until they
   *  say — a state the screen shows rather than papers over, because a collection with no folder
   *  cannot be checked for new work and "nothing new" would answer a question never asked. */
  folder_url?: string | null;
  folder_name?: string | null;
}

/** One lesson in the sequence, and how far this cohort has got with it. */
export interface PlanLesson {
  lesson_id: string;
  title: string;
  ordinal: number | null;
  /** Taught or not is `learner_activity`; the rest are counts of papers in states. A lesson with
   *  no task is taught and never scored, which is normal and says so. */
  state: "not_taught" | "taught" | "collecting" | "scored" | "released";
  scored: boolean;
  handed_in?: { have: number; expected: number } | null;
  /** The instrument this lesson is assessed with, by id — what `/rubric` is fetched by. Null for
   *  a lesson that is taught and not scored. */
  task_id?: string | null;
  /** Whether this lesson's scores count toward measurement (the record), or are practice. The
   *  lesson's own, set by whoever wrote it; nobody ticks it on a folder. Every folder of a lesson
   *  with rubrics is scored, whatever it is called. */
  counts?: boolean;
  /** Always null. A lesson is a plan; scores and feedback belong to a collection below it. Kept
   *  in the shape because the server sends it, and a client that invented one would put the same
   *  confusion back. */
  href?: string | null;
  /** What the lesson IS — its summary, and whatever else was recorded about it. */
  about?: UnitAbout;
  /** Every collection of student work this lesson has, in the order it happened. A lesson taught
   *  as a draft and then a final has two; most have one; an untaught lesson has none. */
  work: WorkFolder[];
}

/** What a unit IS, not just what it is called: from `learning_resource.body`, which has carried
 *  the essential question since 0044 and was never served. Absent fields are absent, not null —
 *  a unit nobody has described is `{}`, and the screen says so rather than showing blank labels. */
/** A STANDARD AS THE MODULE TAGS IT. A code on its own — "W.9-10.1a" — is a citation, and a
 *  teacher who does not already know that one by heart cannot read it: it says there is a claim
 *  here without saying what the claim is. What LDC actually carries is the statement the module
 *  focuses on, the grades it is written for, the disciplines that teach it, and where it sits in
 *  its framework, and all of that fits on a line and a half.
 *
 *  Units seeded before this carry bare strings, and both shapes are read — a unit does not become
 *  undescribed because nobody has filled in its standards' text yet. */
/** One block of a handout. The union is in `content/model.ts`; this is the shape the API sends
 *  and the fixtures write, which is the same thing with nothing narrowed. */
export interface ContentBlock {
  type: string;
  text?: string;
  title?: string;
  citation?: string;
  slots?: string[];
  label?: string;
  expected?: string;
  trait_id?: string;
}

export interface UnitStandard {
  code: string;
  /** The document the code belongs to: the C3 Framework, the CCSS literacy standards. */
  framework?: string;
  /** What the standard asks for, in its own words. */
  statement?: string;
  grade?: string;
  disciplines?: string[];
  /** Where in the framework it sits — "Writing Standards > Text Types and Purposes". */
  source?: string;
}

export interface UnitAbout {
  /** A lesson's own description. Units lead with their question; lessons with what they are. */
  summary?: string;
  /** The one skill a lesson teaches, named — and then written out as what a student must be able
   *  to do. A lesson that said only what HAPPENS in it did not say what it was for. */
  skill?: string;
  skill_statement?: string;
  /** The handout, as typed blocks: the prompt, the sources, the organiser, the space to write.
   *  See `content/model.ts` — the shapes are declared in the content schema's `blockTypes`. */
  blocks?: ContentBlock[];
  question?: string;
  teaching_task?: string;
  /** The LDC task template the teaching task was written from — "Task Template A9 —
   *  Argumentation". The prompt is the template filled in; naming it says which kind of writing
   *  this is before anybody reads the prompt. */
  task_template?: string;
  discipline?: string;
  grade_band?: string;
  standards?: (string | UnitStandard)[];
  /** The module's own links, grouped by who each one is for. See `content/model.ts`. */
  resources?: { title: string; url: string; kind: string }[];
  texts?: string[];
  weeks?: number;
  /** The handle an LDC API would fetch this module by. Not a URL yet. */
  source_ref?: string;
}

export interface PlanUnit {
  unit_id: string | null;
  title: string | null;
  about?: UnitAbout;
  lessons: PlanLesson[];
}

/** Which plan is in force for a course, and whose it is. */
export interface PlanHeader {
  plan_name: string;
  /** True when the teacher has their own variant; false when they are on the institution's. */
  is_mine: boolean;
  /** The plan this one is adapted from, when there is one. A district master is usually a variant
   *  of the publisher's, and a variant with no units of its own inherits the ancestor's sequence —
   *  which is worth saying out loud rather than showing an empty plan. */
  adapted_from: string | null;
  /** True when the lessons listed come from an ancestor rather than this plan. */
  sequence_inherited: boolean;
}

/** Evidence pointed at the plan. The whole product is this sentence being actionable.
 *
 *  It is a COUNT AT A LEVEL, never a mean: "14 of 22 papers sat at level 1 on counterclaims".
 *  `_TRAIT_PROFILE` already returns one row per (trait, level), so this is a read of what is
 *  there rather than a new number. */
export interface Signal {
  trait: string;
  level_label: string;
  n: number;
  of: number;
  /** The lessons this evidence came from, so the claim can be checked. */
  from: string[];
  href: string;
}

/** One of the teacher's sections of a course: a cohort. The only level a section appears at,
 *  besides the folders of student work that belong to one. */
export interface CourseSection {
  section_id: string;
  label: string;
  /** Enrolment — what "22 of 26" counts against, not a target anyone set. */
  enrolled: number;
  /** PER SECTION, never merged. A count of papers at a level across two cohorts is a number
   *  about neither of them. */
  signal: Signal | null;
}

/** ONE COHORT (a course, at one school, in one year), with the teacher's sections of it and the
 *  one sequence they share.
 *
 *  This was one entry per section, which listed the same units and lessons once per period and
 *  headed each with a course and then a section that repeated it. A plan belongs to a class;
 *  three periods of US Government are one plan, not three, and the same course at a second
 *  school is a second class with its own plan. */
export interface CoursePlan {
  /** What a new unit or lesson is added to. */
  class_id: string;
  course_id: string;
  course_name: string;
  plan: PlanHeader;
  sections: CourseSection[];
  units: PlanUnit[];
}

export interface HomeView {
  available: boolean;
  /** The year this answer is ABOUT, which is not always the one that was asked for: a year with
   *  no plan falls back rather than rendering an empty tree under a year that looks chosen. */
  school_year: number | null;
  /** The years this teacher has a plan for, newest first. */
  years: number[];
  /** Across every cohort the teacher staffs: one queue, most urgent first. */
  needs: Need[];
  /** One entry per COURSE. The queue merges because a thing to resolve is a thing to resolve;
   *  the plan does not, because a plan belongs to a course and two courses are two sequences.
   *  Merging them would invent a curriculum nobody wrote. */
  courses: CoursePlan[];
}

/** The sentence shown for each reason. The UI owns all copy (docs/STUDENT_LEARNING.md), and these are written
 *  as what happened rather than as a status word: "Two raters disagreed" can be acted on, and
 *  "prior_rater_mismatch" cannot. */
export const NEED_SENTENCE: Record<NeedKind, string> = {
  disagreement: "Two raters disagreed",
  // Nothing is asking for anything — a trigger stopped, and software does not want. The earlier
  // wording ("stopped and asked for you") gave the scorer an intention it does not have, which is
  // how a person ends up trusting a judgement nothing made.
  //
  // It does not end "requires your attention" either: the band is already titled that, and a row
  // that repeats it spends its width saying what the heading said. The row states what happened.
  // NAMES THE STAGE THAT ACTUALLY STOPPED. `held` is `NEEDS_HUMAN` on a criterion — `abstained` or
  // `no_verified_evidence` — which means scoring RAN and two or three traits came back without a
  // level anybody should rely on. "Scoring could not proceed" said the opposite of what happened,
  // and it said it on a row whose own state read "scored — ready for your review". A teacher who
  // meets a paper described two contradictory ways stops believing the reliable half too, which is
  // the worst thing this screen can do while the scores are the thing being judged.
  held: "Some traits came back without a score",
  unnamed: "Nobody is named on this paper",
  // Scoring did proceed and came back with nothing scorable at all, rather than abstaining on some
  // of it. One is a paper to look at; the other is a paper nothing could read.
  not_scorable: "This paper cannot be scored",
  // THE MESSAGE, NOT THE SCORES. `blocked` is `composed -> blocked`: the draft failed a check —
  // an unverified quotation, or a mention of conventions, which is not on the scale. The scores
  // are fine and finished. A teacher moves it into review, which is one click, and `feedback.py`
  // holds toward this on purpose because sending a bad draft is the expensive mistake.
  message_held: "The draft was held — the scores are fine",
  release: "Feedback is ready to go back",
};

/** Most urgent first. Disagreements and holds are a person waiting on a judgement; missing work
 *  and release are a set waiting on a decision. Within a kind, the order the server sent. */
const RANK: NeedKind[] = [
  "disagreement", "held", "not_scorable", "message_held", "unnamed", "release",
];

export function byUrgency(needs: Need[]): Need[] {
  return [...needs].sort((a, b) => RANK.indexOf(a.kind) - RANK.indexOf(b.kind));
}

/** "22 of 26 handed in" — a count of papers, not a percentage. */
export function ofPhrase(of: { have: number; expected: number }): string {
  return `${of.have} of ${of.expected}`;
}

/** What a folder of student work is called, as the teacher wrote it.
 *
 *  THERE IS NO LIST OF ALLOWED NAMES. "Drafts", "Final op-eds", "Attempt 2", "Period 4 make-ups"
 *  — `iteration` is free text on every table that carries it, and the corpus already uses
 *  "anchor". What a folder's work is scored against is its lesson's, whatever the folder is
 *  called.
 *
 *  Capitalised, not re-worded: a name that came back different from the one they typed is a
 *  system that thinks it knows better. */
export function folderLabel(name: string | null | undefined): string {
  const value = (name ?? "").trim();
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** The names already used on this lesson FOR ONE SECTION, lower-cased.
 *
 *  `learner_activity` is unique on (section, lesson, iteration), so "Final" can be taken in
 *  period 4 and free in period 6 — the section is part of the key, and a form that pooled the
 *  names would refuse a folder the database would have accepted. Knowing which are taken lets it
 *  say so before the teacher presses anything. */
export function namesTaken(work: Pick<WorkFolder, "iteration" | "section_id">[],
                           sectionId: string): Set<string> {
  return new Set(work.filter((w) => w.section_id === sectionId)
                     .map((w) => (w.iteration ?? "").trim().toLowerCase())
                     .filter(Boolean));
}
